/**
 * R&D · Blind Panel 360 Workspace (Wave D).
 * Adds click-through drawer with formula/sample/panelists/scores drill.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, ConfidenceBar } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { BLIND_PANELS, BlindPanelResult, RND_PRODUCTS, FORMULAS, SAMPLING_BATCHES, RND_DECISIONS } from "../../mocks/rndData";
import { BATCHES } from "../../mocks/operationsData";
import { Sparkles } from "lucide-react";

const REC_META: Record<string, { bg: string; fg: string }> = {
  advance:     { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
  reformulate: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
  kill:        { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"  },
};
function pname(id: string) { return RND_PRODUCTS.find((p) => p.id === id)?.name ?? id; }

type PnlTab = "overview" | "formula" | "sample" | "panelists" | "scores" | "linkedBatch" | "linkedFormula" | "linkedReport" | "decision" | "audit";

export default function BlindTesting() {
  const [selected, setSelected] = useState<BlindPanelResult | null>(null);
  const [wsTab, setWsTab] = useState<PnlTab>("overview");

  const drill = useMemo(() => {
    if (!selected) return null;
    const formula = FORMULAS.find((f) => f.productId === selected.productId && f.version === selected.version) || FORMULAS.find((f) => f.productId === selected.productId);
    const sample  = SAMPLING_BATCHES.find((s) => s.productId === selected.productId && s.version === selected.version);
    const decision = RND_DECISIONS.find((d) => d.productId === selected.productId);
    const productName = pname(selected.productId);
    // Fuzzy match to physical batch
    const opsBatch = BATCHES.find((b) => b.productName.toLowerCase().includes(productName.toLowerCase()));
    const avgScore = (selected.longevity + selected.sillage + selected.projection + selected.wearability + selected.uniqueness + selected.memorability) / 6;
    // Simple std-dev proxy (deviation vs mean across dimensions)
    const scores = [selected.longevity, selected.sillage, selected.projection, selected.wearability, selected.uniqueness, selected.memorability];
    const variance = scores.reduce((n, v) => n + Math.pow(v - avgScore, 2), 0) / scores.length;
    const deviation = Math.sqrt(variance);
    return { formula, sample, decision, opsBatch, avgScore, deviation };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="rd-blind">
      <Card testId="rd-bl-summary">
        <SectionHeader eyebrow="Panels · click card to drill" title={`${BLIND_PANELS.length} blind evaluation${BLIND_PANELS.length === 1 ? "" : "s"}`} action={<Sparkles size={14} style={{ color: "var(--ph-ai)" }}/>} />
        <DocumentActions docKey="blind-panel-report" testId="blind-doc-actions" />
        <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>
          Anonymous panels A (12u), B (24u), and C (40u). Overall score ≥ 8.0 clears launch; 6.5–7.9 triggers reformulation; below 6.5 kills the project.
        </div>
      </Card>

      <div className="space-y-4">
        {BLIND_PANELS.map((b) => {
          const m = REC_META[b.recommendation];
          return (
            <Card key={b.id} testId={`rd-bl-${b.id}`}>
              <button
                data-testid={`rd-bl-open-${b.id}`}
                onClick={() => { setSelected(b); setWsTab("overview"); }}
                className="w-full text-left"
                style={{ background: "transparent", border: 0 }}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Panel {b.panel} · {b.version}</div>
                    <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.1rem", color: "var(--ph-ink-strong)" }}>{pname(b.productId)}</div>
                  </div>
                  <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{b.recommendation}</span>
                </div>
                <div className="text-[11px] mb-3" style={{ color: "var(--ph-ink-muted)" }}>{b.responsesIn}/{b.panelSize} responses · overall {b.overall.toFixed(1)}/10</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                  {(
                    [
                      ["Longevity",    b.longevity],
                      ["Sillage",      b.sillage],
                      ["Projection",   b.projection],
                      ["Wearability",  b.wearability],
                      ["Uniqueness",   b.uniqueness],
                      ["Memorability", b.memorability],
                    ] as const
                  ).map(([label, v]) => (
                    <div key={label}>
                      <div className="flex justify-between text-[10px]" style={{ color: "var(--ph-ink-muted)" }}><span>{label}</span><span className="tabular-nums">{v.toFixed(1)}</span></div>
                      <ConfidenceBar value={v/10} />
                    </div>
                  ))}
                </div>
                <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                  <span className="text-[10px] uppercase tracking-[0.14em] mr-2" style={{ color: "var(--ph-ink-muted)" }}>Top comment</span>
                  {b.topComment}
                </div>
              </button>
              <div className="mt-3 flex items-center gap-3 p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)" }} data-testid={`rd-bl-qr-${b.id}`}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=${encodeURIComponent(window.location.origin + "/pureh/blind/scan/" + b.id)}`}
                  alt={`QR ${b.id}`}
                  width={72} height={72}
                  style={{ borderRadius: "var(--ph-radius-sm)" }}
                />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Panelist scan code</div>
                  <div className="text-xs break-all" style={{ color: "var(--ph-ink-default)" }}>
                    <a href={`/pureh/blind/scan/${b.id}`} style={{ color: "var(--ph-accent)" }} data-testid={`rd-bl-scanlink-${b.id}`}>/pureh/blind/scan/{b.id}</a>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid={`rd-bl-docs-${b.id}`}>
                <DocumentActions docKey="blind-sample-report" recordId={b.id} testId={`rd-bl-single-sample-${b.id}`} compact />
                <DocumentActions docKey="blind-panel-report"  recordId={b.id} testId={`rd-bl-single-panel-${b.id}`}  compact />
              </div>
            </Card>
          );
        })}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Blind Panel 360" testId="rd-bl-drawer" width={720}>
        {selected && drill && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: REC_META[selected.recommendation].fg }}>{selected.recommendation}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{pname(selected.productId)} · Panel {selected.panel}</div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Overall</div><div className="text-sm font-semibold tabular-nums">{selected.overall.toFixed(1)}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Average</div><div className="text-sm font-semibold tabular-nums">{drill.avgScore.toFixed(1)}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Deviation</div><div className="text-sm font-semibold tabular-nums">{drill.deviation.toFixed(2)}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Responses</div><div className="text-sm font-semibold tabular-nums">{selected.responsesIn}/{selected.panelSize}</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<PnlTab>
                testId="rd-bl-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",     label: "Overview"     },
                  { value: "formula",      label: "Formula"      },
                  { value: "sample",       label: "Sample"       },
                  { value: "panelists",    label: "Panelists"    },
                  { value: "scores",       label: "Scores"       },
                  { value: "linkedBatch",  label: "Batch"        },
                  { value: "linkedFormula",label: "Linked Formula"},
                  { value: "linkedReport", label: "Report"       },
                  { value: "decision",     label: "Decision"     },
                  { value: "audit",        label: "Audit"        },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="rd-bl-ws-overview">
                <Field label="Panel"          inline><span className="text-sm">{selected.panel}</span></Field>
                <Field label="Version"        inline><span className="text-sm">{selected.version}</span></Field>
                <Field label="Product"        inline><span className="text-sm">{pname(selected.productId)}</span></Field>
                <Field label="Recommendation" inline><span className="text-sm font-semibold" style={{ color: REC_META[selected.recommendation].fg }}>{selected.recommendation}</span></Field>
                <Field label="Top comment"><span className="text-sm">{selected.topComment}</span></Field>
              </div>
            )}

            {wsTab === "formula" && (
              <div data-testid="rd-bl-ws-formula">
                <Field label="Formula version" inline><span className="text-sm">{selected.version}</span></Field>
                {drill.formula && <>
                  <Field label="Cost / L"  inline><span className="text-sm tabular-nums">EGP {drill.formula.costEGPPerLiter.toLocaleString()}</span></Field>
                  <Field label="IFRA"      inline><span className="text-sm" style={{ color: drill.formula.ifraCompliant ? "var(--ph-success)" : "var(--ph-danger)" }}>{drill.formula.ifraCompliant ? "OK" : "FAIL"}</span></Field>
                </>}
              </div>
            )}

            {wsTab === "sample" && (
              <div data-testid="rd-bl-ws-sample">
                {drill.sample ? <>
                  <Field label="Sample batch" inline><span className="text-sm font-mono">{drill.sample.code}</span></Field>
                  <Field label="Units"        inline><span className="text-sm tabular-nums">{drill.sample.units}</span></Field>
                  <Field label="Status"       inline><span className="text-sm">{drill.sample.status}</span></Field>
                </> : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No sampling batch linked yet.</div>}
              </div>
            )}

            {wsTab === "panelists" && (
              <div data-testid="rd-bl-ws-panelists">
                <Field label="Panel size" inline><span className="text-sm tabular-nums">{selected.panelSize}</span></Field>
                <Field label="Responses" inline><span className="text-sm tabular-nums">{selected.responsesIn}</span></Field>
                <Field label="Response rate" inline><span className="text-sm tabular-nums">{Math.round((selected.responsesIn / Math.max(1, selected.panelSize)) * 100)}%</span></Field>
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Individual panelist identities are held anonymous per protocol; only aggregate scores are exposed.</div>
              </div>
            )}

            {wsTab === "scores" && (
              <div data-testid="rd-bl-ws-scores">
                {([
                  ["Longevity",    selected.longevity],
                  ["Sillage",      selected.sillage],
                  ["Projection",   selected.projection],
                  ["Wearability",  selected.wearability],
                  ["Uniqueness",   selected.uniqueness],
                  ["Memorability", selected.memorability],
                ] as const).map(([label, v]) => (
                  <div key={label} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                    <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{label}</div>
                    <ConfidenceBar value={v/10} />
                    <div className="text-sm tabular-nums text-right">{v.toFixed(1)}</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "linkedBatch" && (
              <div data-testid="rd-bl-ws-batch">
                {drill.opsBatch ? <>
                  <Field label="Batch" inline><span className="text-sm font-mono">{drill.opsBatch.code}</span></Field>
                  <Field label="Stage" inline><span className="text-sm">{drill.opsBatch.stage}</span></Field>
                  <Field label="Units" inline><span className="text-sm tabular-nums">{drill.opsBatch.units}</span></Field>
                </> : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No production batch linked yet.</div>}
              </div>
            )}

            {wsTab === "linkedFormula" && (
              <div data-testid="rd-bl-ws-lformula">
                {drill.formula ? <>
                  <Field label="Formula ID"    inline><span className="text-sm font-mono">{drill.formula.id}</span></Field>
                  <Field label="Status"        inline><span className="text-sm">{drill.formula.status}</span></Field>
                  <Field label="Cost / L"      inline><span className="text-sm tabular-nums">EGP {drill.formula.costEGPPerLiter.toLocaleString()}</span></Field>
                </> : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No formula linked.</div>}
              </div>
            )}

            {wsTab === "linkedReport" && (
              <div data-testid="rd-bl-ws-report">
                <DocumentActions docKey="blind-sample-report" recordId={selected.id} testId={`rd-bl-lreport-sample-${selected.id}`} compact />
                <DocumentActions docKey="blind-panel-report"  recordId={selected.id} testId={`rd-bl-lreport-panel-${selected.id}`}  compact />
              </div>
            )}

            {wsTab === "decision" && (
              <div data-testid="rd-bl-ws-decision">
                {drill.decision ? <>
                  <Field label="Decision"   inline><span className="text-sm font-semibold">{drill.decision.decision}</span></Field>
                  <Field label="Decided by" inline><span className="text-sm">{drill.decision.decidedBy}</span></Field>
                  <Field label="When"       inline><span className="text-sm">{drill.decision.dateRelative}</span></Field>
                  <Field label="Rationale"><span className="text-sm">{drill.decision.rationale}</span></Field>
                </> : <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No formal decision recorded yet.</div>}
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="rd-bl-ws-audit">
                <AuditTrail collection="blind_panels" recordId={selected.id} testId="rd-bl-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
